import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

// ============================================================
// VALIDATION HELPERS
// ============================================================
const validatePassword = (value) => {
  if (!value) return 'New password is required.';
  if (value.length < 8) return 'Password must be at least 8 characters.';
  if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter.';
  if (!/[0-9]/.test(value)) return 'Password must contain at least one number.';
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
    return 'Password must contain at least one special character.';
  }
  return '';
};

const validateConfirmPassword = (value, password) => {
  if (!value) return 'Please confirm your new password.';
  if (value !== password) return 'Passwords do not match.';
  return '';
};

// ============================================================
// RESET PASSWORD COMPONENT
// ============================================================
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';

  const [resetToken, setResetToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});

  // --- Handle input changes ---
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'resetToken') setResetToken(value);
    if (name === 'newPassword') {
      setNewPassword(value);
      // Re-validate confirm if touched
      if (touched.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: validateConfirmPassword(confirmPassword, value) }));
      }
    }
    if (name === 'confirmPassword') setConfirmPassword(value);
  };

  // --- Mark field as touched (on blur) ---
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    let err = '';
    if (name === 'resetToken') err = value.trim() ? '' : 'Reset token is required.';
    if (name === 'newPassword') err = validatePassword(value);
    if (name === 'confirmPassword') err = validateConfirmPassword(value, newPassword);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  // --- Validate all ---
  const validateAll = () => {
    const newErrors = {
      resetToken: resetToken.trim() ? '' : 'Reset token is required.',
      newPassword: validatePassword(newPassword),
      confirmPassword: validateConfirmPassword(confirmPassword, newPassword),
    };
    setErrors(newErrors);
    setTouched({ resetToken: true, newPassword: true, confirmPassword: true });
    return !Object.values(newErrors).some(Boolean);
  };

  // --- Submit handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setSuccess(false);

    if (!validateAll()) return;

    setLoading(true);
    try {
      await api.auth.resetPassword({
        resetToken: resetToken.trim(),
        newPassword: newPassword,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'Password reset failed. Please try again.';
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // STYLES
  // ============================================================
  const colors = {
    bg: '#0a0a0a',
    inputBg: '#1a1a1a',
    border: '#2a2a2a',
    borderFocus: '#00d4aa',
    primary: '#00d4aa',
    error: '#ff4d4d',
    errorBg: 'rgba(255, 77, 77, 0.1)',
    success: '#00d4aa',
    successBg: 'rgba(0, 212, 170, 0.1)',
    textPrimary: '#ffffff',
    textSecondary: '#888888',
  };

  const inputBase = {
    padding: '14px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.textPrimary,
    fontSize: '15px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
  };

  const fieldGap = '18px';

  return (
    <div style={{
      maxWidth: '440px',
      margin: '0 auto',
      padding: '40px 20px 60px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: colors.textPrimary,
          marginBottom: '6px',
        }}>
          Reset Password
        </h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary }}>
          Enter your reset token and choose a new password
        </p>
      </div>

      {/* Success State */}
      {success && (
        <div style={{
          background: colors.successBg,
          border: `1px solid ${colors.success}`,
          padding: '20px',
          borderRadius: '10px',
          textAlign: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
          <p style={{ fontWeight: '600', color: colors.textPrimary, marginBottom: '8px' }}>
            Password Reset Successful
          </p>
          <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '16px' }}>
            You can now log in with your new password.
          </p>
          <Link
            to="/login"
            style={{
              display: 'inline-block',
              background: colors.primary,
              color: '#000',
              padding: '12px 28px',
              borderRadius: '8px',
              fontWeight: '700',
              textDecoration: 'none',
              fontSize: '15px',
            }}
          >
            Go to Login
          </Link>
        </div>
      )}

      {/* Server Error Banner */}
      {serverError && !success && (
        <div style={{
          background: colors.errorBg,
          border: `1px solid ${colors.error}`,
          color: colors.error,
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span>⚠️</span> {serverError}
        </div>
      )}

      {/* Form (hidden after success) */}
      {!success && (
        <form onSubmit={handleSubmit} noValidate>
          {/* Reset Token */}
          <div style={{ marginBottom: fieldGap }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '600',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Reset Token
            </label>
            <input
              type="text"
              name="resetToken"
              value={resetToken}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Paste your reset token here"
              autoFocus={!tokenFromUrl}
              style={{
                ...inputBase,
                borderColor: touched.resetToken && errors.resetToken ? colors.error : colors.border,
                fontFamily: 'monospace',
                fontSize: '13px',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = touched.resetToken && errors.resetToken ? colors.error : colors.borderFocus;
                e.target.style.boxShadow = `0 0 0 3px ${touched.resetToken && errors.resetToken ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = 'none';
                handleBlur(e);
              }}
            />
            <div style={{ fontSize: '12px', color: colors.error, marginTop: '4px', minHeight: '16px' }}>
              {touched.resetToken && errors.resetToken}
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              Check your email for the reset token.
            </div>
          </div>

          {/* New Password */}
          <div style={{ marginBottom: fieldGap }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '600',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              value={newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Create a new strong password"
              autoComplete="new-password"
              style={{
                ...inputBase,
                borderColor: touched.newPassword && errors.newPassword ? colors.error : colors.border,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = touched.newPassword && errors.newPassword ? colors.error : colors.borderFocus;
                e.target.style.boxShadow = `0 0 0 3px ${touched.newPassword && errors.newPassword ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = 'none';
                handleBlur(e);
              }}
            />
            <div style={{ fontSize: '12px', color: colors.error, marginTop: '4px', minHeight: '16px' }}>
              {touched.newPassword && errors.newPassword}
            </div>
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '600',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Re-enter your new password"
              autoComplete="new-password"
              style={{
                ...inputBase,
                borderColor: touched.confirmPassword && errors.confirmPassword ? colors.error : colors.border,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = touched.confirmPassword && errors.confirmPassword ? colors.error : colors.borderFocus;
                e.target.style.boxShadow = `0 0 0 3px ${touched.confirmPassword && errors.confirmPassword ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = 'none';
                handleBlur(e);
              }}
            />
            <div style={{ fontSize: '12px', color: colors.error, marginTop: '4px', minHeight: '16px' }}>
              {touched.confirmPassword && errors.confirmPassword}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '10px',
              border: 'none',
              background: loading ? '#00b892' : colors.primary,
              color: '#000',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'background 0.2s, opacity 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {loading ? (
              <>
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      )}

      {/* Back to Login */}
      <p style={{
        textAlign: 'center',
        marginTop: '24px',
        fontSize: '14px',
        color: colors.textSecondary,
      }}>
        <Link to="/login" style={{ color: colors.primary, fontWeight: '600', textDecoration: 'none' }}>
          Back to Login
        </Link>
      </p>
    </div>
  );
}